import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Polygon, CircleMarker, Tooltip, GeoJSON, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import propertiesService from '../services/propertiesService';
import noaaService from '../services/noaaService';
import firmsService from '../services/firmsService';
import nifcService from '../services/nifcService';
import { useAuth } from '../contexts/AuthContext';
import MapOverlaySelector from '../components/MapOverlaySelector';
import MapStyleSelector from '../components/MapStyleSelector';
import MapLegend from '../components/MapLegend';
import FireTooltip from '../components/FireTooltip';
import { DEFAULT_MAP_STYLE, getMapStyleByKey } from '../constants/mapStyles';
import './Dashboard.css';

async function fetchNWSWeather(lat, lon) {
  const pts = await fetch(
    `https://api.weather.gov/points/${lat.toFixed(4)},${lon.toFixed(4)}`
  ).then(r => r.json());

  const [forecast, alertsRes] = await Promise.all([
    fetch(pts.properties.forecastHourly).then(r => r.json()),
    fetch(`https://api.weather.gov/alerts/active?point=${lat.toFixed(4)},${lon.toFixed(4)}`).then(r => r.json()),
  ]);

  const p = forecast.properties.periods[0];
  return {
    temp:          p.temperature,
    windSpeed:     p.windSpeed,
    windDir:       p.windDirection,
    humidity:      p.relativeHumidity?.value ?? '—',
    shortForecast: p.shortForecast,
    alerts:        alertsRes.features || [],
  };
}

/** Pans and zooms the map to the given {lat, lng} once it's available. */
function MapCenterer({ location }) {
  const map = useMap();
  useEffect(() => {
    if (location) map.setView([location.lat, location.lng], 10);
  }, [location, map]);
  return null;
}

/** Blue pulsing home-base pin. */
function HomePin({ location, label }) {
  if (!location) return null;
  return (
    <CircleMarker
      center={[location.lat, location.lng]}
      radius={10}
      pathOptions={{ color: '#0a0a08', fillColor: '#fbff20', fillOpacity: 0.85, weight: 2 }}
    >
      <Tooltip direction="top" offset={[0, -10]} permanent={false}>
        🏠 {label ?? 'Your home base'}
      </Tooltip>
    </CircleMarker>
  );
}

function WeatherWidget({ data, loading, error }) {
  if (loading) return <p className="weather-status">Detecting location…</p>;
  if (error)   return <p className="weather-status weather-error">{error}</p>;
  if (!data)   return null;

  const redFlag  = data.alerts.find(a => /red flag/i.test(a.properties.event ?? ''));
  const otherAlerts = data.alerts
    .filter(a => !/red flag/i.test(a.properties.event ?? ''))
    .slice(0, 2);

  return (
    <div className="weather-content">
      {redFlag && (
        <div className="alert-banner red-flag">
          🚨 {redFlag.properties.headline}
        </div>
      )}
      {otherAlerts.map((a, i) => (
        <div key={i} className="alert-banner">⚠️ {a.properties.headline}</div>
      ))}
      {data.alerts.length === 0 && (
        <div className="alert-banner clear">✅ No active weather alerts</div>
      )}
      <div className="weather-grid">
        <div className="weather-stat">
          <span className="stat-icon">🌡️</span>
          <span className="stat-value">{data.temp}°F</span>
          <span className="stat-label">Temperature</span>
        </div>
        <div className="weather-stat">
          <span className="stat-icon">💨</span>
          <span className="stat-value">{data.windSpeed}</span>
          <span className="stat-label">Wind ({data.windDir})</span>
        </div>
        <div className="weather-stat">
          <span className="stat-icon">💧</span>
          <span className="stat-value">{data.humidity}%</span>
          <span className="stat-label">Humidity</span>
        </div>
        <div className="weather-stat">
          <span className="stat-icon">☀️</span>
          <span className="stat-value stat-forecast">{data.shortForecast}</span>
          <span className="stat-label">Conditions</span>
        </div>
      </div>
    </div>
  );
}

/** Alert polygon styling keyed to NWS event type. */
function alertStyle(feature) {
  const event = (feature.properties?.event || '').toLowerCase();
  if (event.includes('red flag')) {
    return { color: '#e63946', fillColor: '#e63946', fillOpacity: 0.25, weight: 2 };
  }
  if (event.includes('tornado') || event.includes('severe thunderstorm')) {
    return { color: '#f59e0b', fillColor: '#f59e0b', fillOpacity: 0.3, weight: 2 };
  }
  if (event.includes('flash flood') || event.includes('flood')) {
    return { color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.25, weight: 2 };
  }
  if (event.includes('heat') || event.includes('excessive heat')) {
    return { color: '#a855f7', fillColor: '#a855f7', fillOpacity: 0.25, weight: 2 };
  }
  return { color: '#6b7280', fillColor: '#6b7280', fillOpacity: 0.2, weight: 2 };
}

/** Bind a popup to each NWS alert polygon. */
function onEachAlert(feature, layer) {
  const p = feature.properties || {};
  const expires = p.expires ? new Date(p.expires).toLocaleString() : '—';
  const description = p.description
    ? p.description.substring(0, 240).replace(/\s+\S*$/, '') + '…'
    : '';

  layer.bindPopup(`
    <div class="alert-popup">
      <h4>${p.headline || p.event || 'Weather Alert'}</h4>
      <p class="alert-popup-meta"><strong>Expires:</strong> ${expires}</p>
      ${description ? `<p class="alert-popup-desc">${description}</p>` : ''}
      ${p.instruction ? `<p class="alert-popup-instruction"><strong>Instruction:</strong> ${p.instruction.substring(0, 200)}…</p>` : ''}
    </div>
  `);
}

const DROUGHT_LABELS = {
  0: 'Abnormally Dry',
  1: 'Moderate Drought',
  2: 'Severe Drought',
  3: 'Extreme Drought',
  4: 'Exceptional Drought',
};

const DROUGHT_COLORS = {
  0: '#ffff00',
  1: '#fcd37f',
  2: '#ffaa00',
  3: '#e60000',
  4: '#730000',
};

/** Drought polygon styling keyed to USDM category. */
function droughtStyle(feature) {
  const dm = feature.properties?.dm ?? 0;
  const color = DROUGHT_COLORS[dm] ?? '#6b7280';
  return { color, fillColor: color, fillOpacity: 0.35, weight: 1.5 };
}

/** Bind a popup to each USDM drought polygon. */
function onEachDrought(feature, layer) {
  const p = feature.properties || {};
  const dm = p.dm ?? 0;
  const label = DROUGHT_LABELS[dm] ?? 'Unknown';
  const date = p.date ? new Date(p.date).toLocaleDateString() : '—';

  layer.bindPopup(`
    <div class="drought-popup">
      <h4>D${dm} — ${label}</h4>
      <p class="drought-popup-date"><strong>Valid:</strong> ${date}</p>
    </div>
  `);
}

/** NIFC / WFIGS fire-perimeter style. */
function nifcStyle() {
  return { color: '#b91c1c', fillColor: '#ef4444', fillOpacity: 0.25, weight: 1.5 };
}

function formatNifcDate(value) {
  if (value == null) return '—';
  const date = typeof value === 'number' ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

/** Bind a popup to each NIFC fire perimeter. */
function onEachNIFC(feature, layer) {
  const p = feature.properties || {};
  const name = p.attr_IncidentName || p.poly_IncidentName || 'Unnamed fire';
  const acres = p.attr_IncidentSize != null
    ? `${p.attr_IncidentSize} acres`
    : p.poly_GISAcres != null
      ? `${Number(p.poly_GISAcres).toFixed(2)} acres`
      : 'Size unknown';
  const containment = p.attr_PercentContained != null
    ? `${p.attr_PercentContained}% contained`
    : 'Containment unknown';
  const discovered = formatNifcDate(p.attr_FireDiscoveryDateTime);
  const type = p.attr_IncidentTypeCategory || '—';
  const location = [p.attr_POOState, p.attr_POOCounty].filter(Boolean).join(' · ') || '—';

  layer.bindPopup(`
    <div class="nifc-popup">
      <h4>${name}</h4>
      <p><strong>Acres:</strong> ${acres}</p>
      <p><strong>Containment:</strong> ${containment}</p>
      <p><strong>Discovered:</strong> ${discovered}</p>
      <p><strong>Type:</strong> ${type}</p>
      <p><strong>Location:</strong> ${location}</p>
    </div>
  `);
}

function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const homeLocation = user?.location ?? null;
  const [weather, setWeather]               = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [weatherError, setWeatherError]     = useState(null);

  const [properties, setProperties]         = useState([]);
  const [userLocation, setUserLocation]     = useState(null);

  const [mapOverlay, setMapOverlay]         = useState('none');
  const [overlayData, setOverlayData]       = useState(null);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [overlayError, setOverlayError]       = useState(null);

  const [mapStyleKey, setMapStyleKey]       = useState(DEFAULT_MAP_STYLE.key);
  const activeMapStyle                       = getMapStyleByKey(mapStyleKey);

  useEffect(() => {
    propertiesService.getProperties().then(setProperties).catch(() => {});
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      setWeatherError('Geolocation not supported by your browser.');
      setWeatherLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        setUserLocation({ lat: coords.latitude, lng: coords.longitude });
        try {
          const data = await fetchNWSWeather(coords.latitude, coords.longitude);
          setWeather(data);
        } catch {
          setWeatherError('Could not load weather data from NWS.');
        } finally {
          setWeatherLoading(false);
        }
      },
      () => {
        setWeatherError('Location access denied — allow location for local weather.');
        setWeatherLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    if (mapOverlay === 'none') {
      setOverlayData(null);
      setOverlayError(null);
      return;
    }

    let cancelled = false;
    setOverlayLoading(true);
    setOverlayError(null);
    setOverlayData(null); // clear stale data from the previous overlay

    (async () => {
      try {
        let data = null;
        if (mapOverlay === 'alerts') {
          data = await noaaService.fetchNWSAlertsGeoJSON();
        } else if (mapOverlay === 'drought') {
          data = await noaaService.fetchUSDMGeoJSON();
        } else if (mapOverlay === 'fires') {
          const center = userLocation ?? homeLocation ?? { lat: 41.5, lng: -99.9 };
          const bbox = firmsService.bboxAround(center, 2.5);
          data = await firmsService.fetchFIRMSFiresGeoJSON(bbox);
        } else if (mapOverlay === 'nifc') {
          const center = userLocation ?? homeLocation ?? { lat: 41.5, lng: -99.9 };
          const bbox = nifcService.bboxAround(center, 5.0);
          data = await nifcService.fetchNIFCFiresGeoJSON(bbox);
        }
        if (!cancelled) setOverlayData(data);
      } catch (err) {
        if (!cancelled) setOverlayError(err.message || `Could not load ${mapOverlay} overlay.`);
      } finally {
        if (!cancelled) setOverlayLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [mapOverlay, userLocation, homeLocation]);

  return (
    <div className="dashboard-page">
      <div className="dashboard-inner">

        <div className="dashboard-header">
          <h1>My Dashboard</h1>
          <p>Welcome back -- here's what's happening near you.</p>
        </div>

        <div className="dashboard-grid">

          {/* Map */}
          <div className="dashboard-card map-card">
            <h2 className="card-title">Map Overlays</h2>
            <MapOverlaySelector
              active={mapOverlay}
              onChange={setMapOverlay}
              loading={overlayLoading}
            />
            {overlayError && (
              <p className="overlay-error">{overlayError}</p>
            )}
            <div className="map-wrapper">
              <MapStyleSelector active={mapStyleKey} onChange={setMapStyleKey} />
              <MapContainer
                center={[41.5, -99.9]} zoom={10}
                style={{ height: '440px', width: '100%' }}
                scrollWheelZoom={false}
              >
                <MapCenterer location={userLocation ?? homeLocation} />
                <HomePin location={homeLocation} label={user?.city ? `${user.city}, ${user.state}` : 'Your home base'} />
                <TileLayer
                  key={activeMapStyle.key}
                  url={activeMapStyle.url}
                  attribution={activeMapStyle.attribution}
                />
                {properties.map(p =>
                  p.boundary?.length >= 3 && (
                    <Polygon
                      key={p.id}
                      positions={p.boundary.map(pt => [pt.lat, pt.lng])}
                      pathOptions={{ color: '#fe3130', fillColor: '#fe3130', fillOpacity: 0.15, weight: 2 }}
                    />
                  )
                )}
                {mapOverlay === 'alerts' && overlayData?.features && (
                  <GeoJSON
                    key={`nws-alerts-${overlayData.features.length}`}
                    data={overlayData}
                    style={alertStyle}
                    onEachFeature={onEachAlert}
                  />
                )}
                {mapOverlay === 'drought' && overlayData?.features && (
                  <GeoJSON
                    key={`usdm-drought-${overlayData.features.length}`}
                    data={overlayData}
                    style={droughtStyle}
                    onEachFeature={onEachDrought}
                  />
                )}
                {mapOverlay === 'nifc' && overlayData?.features && (
                  <GeoJSON
                    key={`nifc-fires-${overlayData.features.length}`}
                    data={overlayData}
                    style={nifcStyle}
                    onEachFeature={onEachNIFC}
                  />
                )}
                {mapOverlay === 'fires' && overlayData?.features && (
                  overlayData.features.map((feature) => {
                    const { coordinates } = feature.geometry || {};
                    if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
                    const [lng, lat] = coordinates;
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

                    const p = feature.properties || {};
                    return (
                      <CircleMarker
                        key={`fire-${lat}-${lng}`}
                        center={[lat, lng]}
                        radius={4}
                        pathOptions={{
                          color: '#b91c1c',
                          fillColor: '#ff4500',
                          fillOpacity: 0.9,
                          weight: 1,
                        }}
                      >
                        <Tooltip direction="top" offset={[0, -4]}>
                          <FireTooltip properties={p} />
                        </Tooltip>
                      </CircleMarker>
                    );
                  })
                )}
              </MapContainer>
              <MapLegend overlay={mapOverlay} />
            </div>
          </div>

          {/* ── Right column ──────────────────────── */}
          <div className="dashboard-right-col">

            <div className="dashboard-card weather-card">
              <h2 className="card-title">🌤️ Weather &amp; Alerts</h2>
              <WeatherWidget data={weather} loading={weatherLoading} error={weatherError} />
            </div>

            <div className="dashboard-card actions-card">
              <h2 className="card-title">Quick Actions</h2>
              <div className="action-buttons">
                <button className="action-btn" onClick={() => navigate('/properties')}>
                  Add a New Property
                </button>
                <button className="action-btn action-btn-alert" onClick={() => navigate('/create-alert')}>
                   Create An Alert!
                </button>
                <button className="action-btn action-btn-alerts" onClick={() => navigate('/alerts')}>
                   View Alerts
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* ── My Properties ─────────────────────────── */}
        {properties.length > 0 && (
          <div className="dashboard-properties">
            <h2 className="dashboard-section-title">My Properties</h2>
            <div className="dashboard-properties-grid">
              {properties.map(p => (
                <div key={p.id} className="dashboard-property-card" onClick={() => navigate(`/properties/${p.id}`)}>
                  <h3 className="dp-name">{p.name}</h3>
                  {p.description && <p className="dp-description">{p.description}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default Dashboard;

